// Minimal toast / notification system.
//
// One bottom-right stack. Three intents (success / error / info).
// Auto-dismiss for success+info; errors stick until explicit dismiss
// (so the user has time to read what went wrong). Designed to plug
// into a single `<Toaster />` mount in the Layout.
//
// Why hand-rolled vs. a library: the surface area is tiny (push +
// dismiss + auto-expire) and a library would pull in styling +
// animation infrastructure we don't need. ~80 lines total here.

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { createElement } from 'react';

export type ToastIntent = 'success' | 'error' | 'info';

export type Toast = {
  id: number;
  intent: ToastIntent;
  message: string;
  // Optional sub-message for additional detail. Renders below the
  // primary message in muted text.
  detail?: string;
};

type ToastApi = {
  push: (toast: Omit<Toast, 'id'>) => number;
  dismiss: (id: number) => void;
  toasts: ReadonlyArray<Toast>;
};

const ToastContext = createContext<ToastApi | null>(null);

let nextId = 1;

const AUTO_DISMISS_MS: Record<ToastIntent, number | null> = {
  success: 4000,
  info: 4000,
  // Errors don't auto-dismiss — user might want to copy/screenshot.
  error: null,
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast: Omit<Toast, 'id'>): number => {
      const id = nextId++;
      const next: Toast = { id, ...toast };
      setToasts((prev) => [...prev, next]);
      const ttl = AUTO_DISMISS_MS[toast.intent];
      if (ttl !== null) {
        setTimeout(() => dismiss(id), ttl);
      }
      return id;
    },
    [dismiss],
  );

  return createElement(ToastContext.Provider, { value: { push, dismiss, toasts } }, children);
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Outside a ToastProvider, fall back to console — keeps unit
    // tests + non-Layout-wrapped pages from crashing.
    return {
      push: (toast) => {
        const fn =
          toast.intent === 'error' ? console.error : toast.intent === 'success' ? console.log : console.info;
        fn(`[toast.${toast.intent}]`, toast.message, toast.detail ?? '');
        return 0;
      },
      dismiss: () => {},
      toasts: [],
    };
  }
  return ctx;
}
